/**
 * TeamSignalsCard
 * Sortable table showing opponent prop hit rates split by direction (over/under).
 * Click a row to expand per-prop breakdown. Color-coded by classification tier.
 */

import type {
  DirectionalRate,
  EnrichedTeamSignal,
  PropBreakdown,
  SignalClassification,
} from '@parlay-picker/shared/api/mlApi';
import { cn } from '@utils/cn';
import { formatPropType } from '@utils/propUtils';
import { ArrowDown, ArrowUp, ArrowUpDown, ChevronRight, Target } from 'lucide-react';
import { Fragment, type ReactElement, useMemo, useState } from 'react';

// =============================================================================
// Constants
// =============================================================================

type SortKey = 'team' | 'hitRate' | 'over' | 'under' | 'total';
type SortDir = 'asc' | 'desc';

const TIER_COLORS: Record<SignalClassification, { text: string; bg: string; badge: string }> = {
  target: {
    text: 'text-emerald-400',
    bg: 'bg-emerald-500/10',
    badge: 'bg-emerald-500/20 text-emerald-400',
  },
  favorable: {
    text: 'text-green-400',
    bg: 'bg-green-500/10',
    badge: 'bg-green-500/20 text-green-400',
  },
  neutral: {
    text: 'text-theme-text-secondary',
    bg: '',
    badge: 'bg-theme-border/30 text-theme-text-secondary',
  },
  caution: {
    text: 'text-orange-400',
    bg: 'bg-orange-500/10',
    badge: 'bg-orange-500/20 text-orange-400',
  },
  avoid: { text: 'text-red-400', bg: 'bg-red-500/10', badge: 'bg-red-500/20 text-red-400' },
};

function rateColor(classification: SignalClassification): string {
  return TIER_COLORS[classification].text;
}

// =============================================================================
// Sub-components
// =============================================================================

function SortIcon({
  sortKey,
  currentKey,
  sortDir,
}: {
  sortKey: SortKey;
  currentKey: SortKey;
  sortDir: SortDir;
}): ReactElement {
  if (sortKey !== currentKey) return <ArrowUpDown className="w-3 h-3 opacity-30" />;
  return sortDir === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />;
}

function RateCell({
  rate,
  className,
}: {
  rate: DirectionalRate | null;
  className?: string;
}): ReactElement {
  if (!rate) return <span className="text-theme-text-tertiary">—</span>;
  return (
    <span className={cn('font-mono text-xs', rateColor(rate.classification), className)}>
      {rate.hitRate.toFixed(1)}%
    </span>
  );
}

function PropRow({ prop }: { prop: PropBreakdown }): ReactElement {
  return (
    <tr className="border-b border-theme-border/5">
      <td className="py-1 pl-8 pr-2 text-xs text-theme-text-secondary">
        {formatPropType(prop.propType)}
      </td>
      <td className={cn('py-1 pr-2 text-right font-mono text-xs', rateColor(prop.classification))}>
        {prop.hitRate.toFixed(1)}%
      </td>
      <td className="py-1 pr-2 text-right">
        <RateCell rate={prop.over} />
      </td>
      <td className="py-1 pr-2 text-right">
        <RateCell rate={prop.under} />
      </td>
      <td className="py-1 text-right text-[10px] text-theme-text-tertiary">{prop.total}</td>
    </tr>
  );
}

// =============================================================================
// Main Component
// =============================================================================

export function TeamSignalsCard({
  teams,
  subtitle = '(prop hit rate vs opponent)',
}: {
  teams: EnrichedTeamSignal[];
  subtitle?: string;
}): ReactElement {
  const [sortKey, setSortKey] = useState<SortKey>('hitRate');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [expandedTeam, setExpandedTeam] = useState<string | null>(null);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(key === 'team' ? 'asc' : 'desc');
    }
  };

  const sortedTeams = useMemo(() => {
    return [...teams].sort((a, b) => {
      let cmp = 0;
      switch (sortKey) {
        case 'team':
          cmp = a.opponent.localeCompare(b.opponent);
          break;
        case 'hitRate':
          cmp = a.hitRate - b.hitRate;
          break;
        case 'over':
          cmp = (a.over?.hitRate ?? -1) - (b.over?.hitRate ?? -1);
          break;
        case 'under':
          cmp = (a.under?.hitRate ?? -1) - (b.under?.hitRate ?? -1);
          break;
        case 'total':
          cmp = a.total - b.total;
          break;
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [teams, sortKey, sortDir]);

  const toggleExpand = (opponent: string) => {
    setExpandedTeam((prev) => (prev === opponent ? null : opponent));
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Target className="w-4 h-4 text-theme-text-secondary" />
        <span className="text-xs font-semibold text-theme-text uppercase tracking-wide">
          Team Signals
        </span>
        <span className="text-[10px] text-theme-text-tertiary">{subtitle}</span>
      </div>

      <div className="overflow-auto max-h-[420px] pr-1">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-theme-text-tertiary border-b border-theme-border/30">
              <th className="text-left py-1.5 pr-2 font-medium w-[28%]">
                <button
                  type="button"
                  onClick={() => handleSort('team')}
                  className="flex items-center gap-1 hover:text-theme-text"
                >
                  Team <SortIcon sortKey="team" currentKey={sortKey} sortDir={sortDir} />
                </button>
              </th>
              <th className="text-right py-1.5 pr-2 font-medium">
                <button
                  type="button"
                  onClick={() => handleSort('hitRate')}
                  className="flex items-center gap-1 justify-end hover:text-theme-text"
                >
                  Hit Rate <SortIcon sortKey="hitRate" currentKey={sortKey} sortDir={sortDir} />
                </button>
              </th>
              <th className="text-right py-1.5 pr-2 font-medium">
                <button
                  type="button"
                  onClick={() => handleSort('over')}
                  className="flex items-center gap-1 justify-end hover:text-theme-text"
                >
                  Overs <SortIcon sortKey="over" currentKey={sortKey} sortDir={sortDir} />
                </button>
              </th>
              <th className="text-right py-1.5 pr-2 font-medium">
                <button
                  type="button"
                  onClick={() => handleSort('under')}
                  className="flex items-center gap-1 justify-end hover:text-theme-text"
                >
                  Unders <SortIcon sortKey="under" currentKey={sortKey} sortDir={sortDir} />
                </button>
              </th>
              <th className="text-right py-1.5 font-medium">
                <button
                  type="button"
                  onClick={() => handleSort('total')}
                  className="flex items-center gap-1 justify-end hover:text-theme-text"
                >
                  N <SortIcon sortKey="total" currentKey={sortKey} sortDir={sortDir} />
                </button>
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedTeams.map((team) => {
              const isExpanded = expandedTeam === team.opponent;
              const tierColors = TIER_COLORS[team.classification];
              return (
                <Fragment key={team.opponent}>
                  <tr
                    className={cn(
                      'border-b border-theme-border/10 cursor-pointer hover:bg-theme-border/5 transition-colors',
                      isExpanded && tierColors.bg,
                    )}
                    onClick={() => toggleExpand(team.opponent)}
                  >
                    <td className="py-1.5 pr-2">
                      <div className="flex items-center gap-1.5">
                        <ChevronRight
                          className={cn(
                            'w-3 h-3 text-theme-text-tertiary transition-transform shrink-0',
                            isExpanded && 'rotate-90',
                          )}
                        />
                        <span className="text-theme-text font-medium truncate">
                          {team.opponent}
                        </span>
                        <span
                          className={cn(
                            'text-[9px] font-bold px-1 py-0.5 rounded-full uppercase shrink-0',
                            tierColors.badge,
                          )}
                        >
                          {team.classification}
                        </span>
                      </div>
                    </td>
                    <td
                      className={cn(
                        'py-1.5 pr-2 text-right font-mono font-bold',
                        rateColor(team.classification),
                      )}
                    >
                      {team.hitRate.toFixed(1)}%
                    </td>
                    <td className="py-1.5 pr-2 text-right">
                      <RateCell rate={team.over} />
                    </td>
                    <td className="py-1.5 pr-2 text-right">
                      <RateCell rate={team.under} />
                    </td>
                    <td className="py-1.5 text-right text-theme-text-tertiary">
                      {team.total.toLocaleString()}
                    </td>
                  </tr>
                  {isExpanded && team.propBreakdown.length > 0 && (
                    <tr>
                      <td colSpan={5} className={cn('px-0 py-0', tierColors.bg)}>
                        <table className="w-full text-xs">
                          <tbody>
                            {team.propBreakdown.map((prop) => (
                              <PropRow key={prop.propType} prop={prop} />
                            ))}
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
